const SPACE_MAP = {
  XXL: "var(--space-xxl)",
  XL: "var(--space-xl)",
  L: "var(--space-l)",
  M: "var(--space-m)",
  S: "var(--space-s)",
  XS: "var(--space-xs)",
  XXS: "var(--space-xxs)",
}

const FONT_SIZE_MAP = {
  XXXXL: "var(--font-size-xxxxl)",
  XXXL: "var(--font-size-xxxl)",
  XXL: "var(--font-size-xxl)",
  XL: "var(--font-size-xl)",
  L: "var(--font-size-l)",
  M: "var(--font-size-m)",
  S: "var(--font-size-s)",
  XS: "var(--font-size-xs)",
  XXS: "var(--font-size-xxs)",
}

const FONT_WEIGHT_MAP = {
  light: "var(--font-weight-light)",
  normal: "var(--font-weight-normal)",
  bold: "var(--font-weight-bold)",
  extraBold: "var(--font-weight-extra-bold)",
}

const COLOR_MAP = {
  primary: "var(--color-primary)",
  primaryDarker: "var(--color-primary-darker)",
  primaryBackground: "var(--color-primary-background)",
  primaryBackgroundDarker: "var(--color-primary-background-darker)",
  second: "var(--color-second)",
  third: "var(--color-third)",
  fourth: "var(--color-fourth)",
  border: "var(--color-border)",
  borderLight: "var(--color-border-light)",
  background: "var(--color-background)",
  backgroundDarker: "var(--color-background-darker)",
  backgroundOpacity: "var(--color-background-opacity)",
  white: "var(--color-white)",
  transparent: "var(--color-transparent)",
  purple: "var(--color-purple)",
  blue: "var(--color-blue)",
  red: "var(--color-red)",
  redBackground: "var(--color-red-background)",
  orange: "var(--color-orange)",
  orangeBackground: "var(--color-orange-background)",
  green: "var(--color-green)",
  greenBackground: "var(--color-green-background)",
  greenDarker: "var(--color-green-darker)",
  orangeDarker: "var(--color-orange-darker)",
}

const BORDER_RADIUS_MAP = {
  borderRadius: "var(--border-radius)",
  borderRadiusButton: "var(--border-radius-button)",
  borderRadiusOuter: "var(--border-radius-outer)",
  borderRadiusXSOuter: "var(--border-radius-xs-outer)",
  borderRadiusLarge: "var(--border-radius-large)",
  borderRadiusXLarge: "var(--border-radius-x-large)",
}

function getSpaceValue(value: string | number) {
  if (typeof value === "string" && SPACE_MAP[value as keyof typeof SPACE_MAP]) {
    return SPACE_MAP[value as keyof typeof SPACE_MAP]
  }
  return String(value)
}

function getFontSizeValue(value: string) {
  if (FONT_SIZE_MAP[value as keyof typeof FONT_SIZE_MAP]) {
    return FONT_SIZE_MAP[value as keyof typeof FONT_SIZE_MAP]
  }
  return value
}

function getFontWeight(value: string) {
  if (FONT_WEIGHT_MAP[value as keyof typeof FONT_WEIGHT_MAP]) {
    return FONT_WEIGHT_MAP[value as keyof typeof FONT_WEIGHT_MAP]
  }
  return value
}

function getColorValue(value: string) {
  if (COLOR_MAP[value as keyof typeof COLOR_MAP]) {
    return COLOR_MAP[value as keyof typeof COLOR_MAP]
  }
  return value
}

function getBorderRadiusValue(value: string) {
  if (BORDER_RADIUS_MAP[value as keyof typeof BORDER_RADIUS_MAP]) {
    return BORDER_RADIUS_MAP[value as keyof typeof BORDER_RADIUS_MAP]
  }
  return value
}

export function generateStylesFromProps(props: any) {
  const styles: Record<string, any> = {}

  // Margin properties
  if (props.$margin) styles.margin = getSpaceValue(props.$margin)
  if (props.$marginTop) styles.marginTop = getSpaceValue(props.$marginTop)
  if (props.$marginBottom) styles.marginBottom = getSpaceValue(props.$marginBottom)
  if (props.$marginLeft) styles.marginLeft = getSpaceValue(props.$marginLeft)
  if (props.$marginRight) styles.marginRight = getSpaceValue(props.$marginRight)

  // Padding properties
  if (props.$padding) styles.padding = getSpaceValue(props.$padding)
  if (props.$paddingTop) styles.paddingTop = getSpaceValue(props.$paddingTop)
  if (props.$paddingBottom) styles.paddingBottom = getSpaceValue(props.$paddingBottom)
  if (props.$paddingLeft) styles.paddingLeft = getSpaceValue(props.$paddingLeft)
  if (props.$paddingRight) styles.paddingRight = getSpaceValue(props.$paddingRight)

  // Display
  if (props.$display) styles.display = props.$display

  // Position
  if (props.$position) styles.position = props.$position
  if (props.$top) styles.top = getSpaceValue(props.$top)
  if (props.$left) styles.left = getSpaceValue(props.$left)
  if (props.$bottom) styles.bottom = getSpaceValue(props.$bottom)
  if (props.$right) styles.right = getSpaceValue(props.$right)

  // Opacity
  if (props.$opacity !== undefined && props.$opacity !== null) {
    styles.opacity = props.$opacity
  }

  // Flexbox
  if (props.$flexDirection) styles.flexDirection = props.$flexDirection
  if (props.$flex) styles.flex = props.$flex
  if (props.$justifyContent) styles.justifyContent = props.$justifyContent
  if (props.$alignItems) styles.alignItems = props.$alignItems

  // Size
  if (props.$width) styles.width = props.$width
  if (props.$maxWidth) styles.maxWidth = props.$maxWidth
  if (props.$minWidth) styles.minWidth = props.$minWidth
  if (props.$height) styles.height = props.$height
  if (props.$maxHeight) styles.maxHeight = props.$maxHeight
  if (props.$minHeight) styles.minHeight = props.$minHeight
  if (props.$size) {
    styles.width = getFontSizeValue(props.$size)
    styles.maxWidth = getFontSizeValue(props.$size)
    styles.minWidth = getFontSizeValue(props.$size)
    styles.height = getFontSizeValue(props.$size)
    styles.maxHeight = getFontSizeValue(props.$size)
    styles.minHeight = getFontSizeValue(props.$size)
  }

  // Text
  if (props.$textAlign) styles.textAlign = props.$textAlign
  if (props.$textTransform) styles.textTransform = props.$textTransform
  if (props.$fontSize) styles.fontSize = getFontSizeValue(props.$fontSize)
  if (props.$fontWeight) styles.fontWeight = getFontWeight(props.$fontWeight)

  // Border
  if (props.$borderRadius) styles.borderRadius = getBorderRadiusValue(String(props.$borderRadius))
  if (props.$borderColor) styles.borderColor = getColorValue(props.$borderColor)

  // Background & Color
  if (props.$background) styles.background = getColorValue(props.$background)
  if (props.$color) styles.color = getColorValue(props.$color)

  if (props.$flex) styles.flex = props.$flex

  return styles
}
